import React from 'react';
import classNames from '@utils/classnames';

export interface ToggleGroupOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactElement;
  /** Optional counter shown as muted text after the label. */
  count?: number;
}

interface ToggleGroupProps<T extends string> {
  options: ToggleGroupOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

/** Segmented control — sunken track, the active segment lifts to a white surface. */
export function ToggleGroup<T extends string>({ options, value, onChange, className }: ToggleGroupProps<T>) {
  return (
    <div
      role="group"
      className={classNames(
        'inline-flex h-9 items-center gap-0.5 rounded-md border border-border bg-surface-sunken p-[3px]',
        className,
      )}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isActive}
            className={classNames(
              'flex h-full items-center gap-1.5 rounded-sm px-3 text-xs transition-colors',
              isActive
                ? 'border border-border bg-surface font-semibold text-fg shadow-sm'
                : 'border border-transparent font-medium text-fg-muted hover:text-fg',
            )}
            onClick={() => onChange(option.value)}
          >
            {option.icon && (
              <span
                className={classNames('shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5', isActive ? 'text-fg' : 'text-fg-muted')}
              >
                {option.icon}
              </span>
            )}
            {option.label}
            {option.count != null && <span className="font-medium text-fg-muted">{option.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
